import { apiRequest } from '../../api/client';
// Cùng khái niệm WeekdayMask với M14 (backend M10 cũng dùng lại của M14). Tái dùng danh sách
// thứ đã có thay vì chép bản thứ hai — nhãn thứ hiển thị phải trùng nhau ở mọi màn.
import { WEEKDAY_OPTIONS, type Weekday } from '../contents/api';

export type { Weekday };
export { WEEKDAY_OPTIONS };

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> => promise.then((e) => e.data);

export interface ReservationSlot {
  id: string;
  branchId: string;
  /**
   * ⚠️ Server trả **đủ bảy thứ** khi khung áp mọi ngày (`WeekdayMask.EveryDay` = 127), không trả
   * mảng rỗng — nên chỗ hiển thị phải rút bảy thứ về "Mọi ngày" (xem {@link daysLabel}).
   */
  daysOfWeek: Weekday[];
  /** `HH:mm:ss` từ `TimeOnly` của backend — model không lưu giây. */
  startsAt: string;
  endsAt: string;
  capacity: number;
  isActive: boolean;
}

export interface BranchWithoutSlots {
  branchId: string;
  branchName: string;
}

export interface UpsertReservationSlotPayload {
  branchId: string;
  /** Rỗng = mọi ngày (`WeekdayMask.From` trả `EveryDay`). */
  daysOfWeek: Weekday[];
  startsAt: string;
  endsAt: string;
  capacity: number;
  isActive: boolean;
}

export const reservationSlotKeys = {
  all: ['reservation-slots'] as const,
  list: (branchId?: string) => [...reservationSlotKeys.all, 'list', branchId ?? 'all'] as const,
  coverage: [...['reservation-slots'], 'coverage'] as const,
};

/** `HH:mm:ss` → `HH:mm`. Model không lưu giây nên hiện giây chỉ là tiếng ồn. */
export function formatClock(value: string): string {
  return value.slice(0, 5);
}

const WEEKDAY_SHORT: Record<Weekday, string> = {
  Monday: 'T2',
  Tuesday: 'T3',
  Wednesday: 'T4',
  Thursday: 'T5',
  Friday: 'T6',
  Saturday: 'T7',
  Sunday: 'CN',
};

/** Bảy (hoặc rỗng) thứ rút về "Mọi ngày"; in bảy chip là đúng dữ liệu mà không ai đọc được. */
export function daysLabel(days: Weekday[]): string {
  if (days.length === 0 || days.length === 7) return 'Mọi ngày';
  return days.map((d) => WEEKDAY_SHORT[d]).join(', ');
}

export interface SlotHours {
  label: string;
  /** Giờ kết thúc nhỏ hơn giờ bắt đầu = vắt qua nửa đêm, hợp lệ (quán ăn đêm). */
  overnight: boolean;
}

export function slotHours(slot: ReservationSlot): SlotHours {
  return {
    label: `${formatClock(slot.startsAt)}–${formatClock(slot.endsAt)}`,
    overnight: slot.endsAt < slot.startsAt,
  };
}

function listQuery(branchId?: string): string {
  return branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
}

export const reservationSlotApi = {
  list: (branchId?: string) =>
    unwrap(apiRequest<ReservationSlot[]>(`/admin/reservation-slots${listQuery(branchId)}`)),

  branchesWithoutSlots: () =>
    unwrap(apiRequest<BranchWithoutSlots[]>('/admin/reservation-slots/branches-without-slots')),

  create: (body: UpsertReservationSlotPayload) =>
    unwrap(apiRequest<ReservationSlot>('/admin/reservation-slots', { method: 'POST', body })),

  update: (id: string, body: UpsertReservationSlotPayload) =>
    unwrap(apiRequest<ReservationSlot>(`/admin/reservation-slots/${id}`, { method: 'PUT', body })),

  // Không có DELETE ở backend, và đó là chủ ý: xóa khung có đơn là cắt căn cứ của đơn (FK Restrict).
  activate: (id: string) =>
    unwrap(apiRequest<ReservationSlot>(`/admin/reservation-slots/${id}/activate`, { method: 'POST' })),

  deactivate: (id: string) =>
    unwrap(
      apiRequest<ReservationSlot>(`/admin/reservation-slots/${id}/deactivate`, { method: 'POST' }),
    ),
};
